import React, { useEffect, useState } from 'react';
import { FormDialog, Icon, InputField, MuiBox, SelectDropdown, Tooltip, Typography } from '@kis-technologies/kis-ui';
import { NULL_TICKET_TEMPLATE, NullTicketTemplate } from 'constants/forms';
import { useDecimalField } from 'hooks/useDecimalField';
import { KioskTemplateOverlay } from 'views/KiosksView/components/KioskFormOverlay/KioskTemplateOverlay';
import { useStyles } from '../styled';
import { PriceType, ProductPermissions } from '../types';
import { TicketTemplate } from 'store/ticketTemplates/types';

interface PriceTypeOverlayProps {
  mode: 'create' | 'edit';
  initialData: PriceType | null;
  onClose: () => void;
  onSave: (priceType: PriceType) => void;
  templates: { id: string; name: string }[];
  onTemplatesUpdated: () => void;
  permissions: ProductPermissions;
}

const PriceTypeOverlay: React.FC<PriceTypeOverlayProps> = ({
  mode,
  initialData,
  onClose,
  onSave,
  templates,
  onTemplatesUpdated,
  permissions
}) => {
  const classes = useStyles();

  const [formData, setFormData] = useState<PriceType>({
    id: '',
    name: '',
    price: null,
    ticketTemplateId: null,
    enabled: false
  });
  const [errors, setErrors] = useState({
    name: '',
    price: ''
  });
  const [templateOverlayOpen, setTemplateOverlayOpen] = useState(false);

  const templateOptions = [NULL_TICKET_TEMPLATE, ...templates];

  useEffect(() => {
    if (mode === 'edit' && initialData) {
      setFormData({
        ...initialData
      });
    }
  }, [mode, initialData]);

  const validate = () => {
    const newErrors = { name: '', price: '' };

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.price === null || formData.price === undefined) {
      newErrors.price = 'Price is required';
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.price;
  };

  const handleSave = () => {
    if (!validate()) return;

    onSave({
      ...formData,
      id: formData.id || Date.now().toString()
    });
  };

  const priceField = useDecimalField({
    value: formData.price,
    onChange: (value) => setFormData((prev) => ({ ...prev, price: value })),
    min: 0
  });

  return (
    <FormDialog
      open
      title={mode === 'edit' ? 'Edit Price Type' : 'Create Price Type'}
      confirmButtonAction={handleSave}
      confirmButtonText="Save"
      confirmButtonDisabled={!permissions.canEditPriceType}
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
    >
      <MuiBox mt="10px">
        <MuiBox className={classes.formBox}>
          <InputField
            label="Name"
            size="small"
            value={formData.name}
            error={!!errors.name}
            helperText={errors.name}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                name: e.target.value
              }))
            }
            sx={{ width: '262px', height: '45px' }}
            disabled={!permissions.canEditPriceType}
          />
        </MuiBox>

        <MuiBox className={classes.formBox}>
          <InputField
            label="Price"
            size="small"
            startIcon="$"
            type="text"
            align="right"
            value={priceField.displayedValue}
            error={!!errors.price}
            helperText={errors.price}
            onFocus={priceField.handleFocus}
            onChange={(e) => priceField.handleChange(e.target.value)}
            onBlur={priceField.handleBlur}
            sx={{
              width: { xs: '262px', sm: '175px' },
              height: '45px'
            }}
            disabled={!permissions.canEditPriceType}
            inputProps={{ inputMode: 'decimal' }}
          />
        </MuiBox>

        <MuiBox className={classes.formBox}>
          <Typography
            className={classes.formLabel}
            sx={{ display: 'flex', justifyContent: 'space-between', width: '262px' }}
          >
            Template
            {permissions.canManageTemplates && (
              <Tooltip title={'Manage Templates'} placement="top">
                <Icon
                  name="Settings"
                  sx={{ cursor: 'pointer', mr: '10px' }}
                  onClick={() => {
                    setTemplateOverlayOpen(true);
                  }}
                />
              </Tooltip>
            )}
          </Typography>
          <SelectDropdown
            items={templateOptions}
            itemToString={(item: TicketTemplate | NullTicketTemplate) =>
              `${item.name}${item.type ? ` (${item.type})` : ''}`
            }
            selectedItem={templateOptions.find((t) => t.id === formData.ticketTemplateId) || NULL_TICKET_TEMPLATE}
            onChange={(item: TicketTemplate | NullTicketTemplate) =>
              setFormData((prev) => ({
                ...prev,
                ticketTemplateId: item?.id ?? null
              }))
            }
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: '#000'
              }
            }}
            disabled={!permissions.canEditPriceType}
          />
        </MuiBox>
      </MuiBox>
      {templateOverlayOpen && (
        <KioskTemplateOverlay
          canEdit={permissions.canManageTemplates}
          onClose={() => {
            setTemplateOverlayOpen(false);
            onTemplatesUpdated();
          }}
        />
      )}
    </FormDialog>
  );
};

export default PriceTypeOverlay;
